export interface Supplier {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface Discrepancy {
  hasDiscrepancy: boolean;
  totalExpectedItems: number;
  totalReceivedItems: number;
  totalExpectedQuantity: number;
  totalReceivedQuantity: number;
  positiveDiscrepancyCount: number;
  negativeDiscrepancyCount: number;
}

export interface ShipmentStats {
  [key: string]: any;
}

export interface Shipment {
  id: string;
  refNumber: string;
  supplierId: string;
  supplierName: string;
  expectedDate: string;
  actualArrivalDate?: string | null;
  expectedBoxCount: number;
  receivedBoxCount?: number | null;
  status: ShipmentStatus;
  statusName: string;
  implementationFile?: string;
  report?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  isOverdue: boolean;
  hasDiscrepancy: boolean;
  boxDiscrepancy?: any | null;
  delayDuration?: any | null;
  supplier?: Supplier;
  boxes: BoxProp[];
  discrepancySummary?: Discrepancy;
  stats?: ShipmentStats | null;
  isDraft?: boolean;
  lastSaved?: string;
  totalExpectedItems: number;
  totalReceivedItems: number;
  positiveDiscrepancyCount: number;
  negativeDiscrepancyCount: number;
}

export interface BoxProp {
  id: string;
  refNumber: string;
  barcode: string;
  status: BoxStatus;
  boxItems: BoxItem[];
}

export interface BoxItem {
  id: string;
  barcode: string;
  qty: number;
  unitId?: string;
  unitDetails?: {
    id: string;
    itemId: string;
    itemName: string;
    barcode: string;
    symbol: string;
    unitOfMeasurement: string;
    onHandQuantity: number;
    expectedQuantity: number;
    isSynced: boolean;
    itemAdditionalProperties?: {
      size?: string;
      Brand?: string;
      Color?: string;
      gender?: string;
      season?: string;
      Generic?: string;
      Category?: string;
      [key: string]: any;
    };
    itemMinNumber?: number | null;
    itemMaxNumber?: number | null;
    currentInventoryQuantity: number;
    inventoryLocationCount: number;
    inventoryLocations?: Array<{
      warehouseId: string;
      warehouseName: string;
      storageUnitId: string;
      storageUnitName: string;
      quantity: number;
    }>;
  };
}

export enum ShipmentStatus {
  Processing = 0,
  Expected = 1,
  InReception = 2,
  Received = 3,
  Allocated = 4,
  Cancelled = 5,
  Analyzing = 6,
  Analyzed = 7,
  Completing = 8,
  Completed = 9
}

export enum BoxStatus {
  New = 0,
  Paused = 1,
  Completed = 2
}

// Extended item type for details view
export interface ExtendedBoxItem extends BoxItem {
  boxId: string;
  boxRefNumber: string;
  boxBarcode: string;
  boxStatus: BoxStatus;
}

// Paginated response types
export interface PagedResult<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// Shipment Item DTO
export interface ShipmentItemDto {
  id: string;
  shipmentId: string;
  unitId?: string;
  barcode: string;
  expectedQuantity: number;
  receivedQuantity: number;
  discrepancyQuantity: number;
  discrepancyType?: 'Positive' | 'Negative' | null;
  discrepancyTypeName?: string;
  boxReferences?: string[];
  createdAt: string;
  updatedAt: string;
}

// Box DTO with optional unit details
export interface BoxDto {
  id: string;
  refNumber: string;
  barcode: string;
  status: BoxStatus;
  boxItems: BoxItem[];
  unitDetails?: any; // Optional unit details when includeUnitDetails=true
}

// Analysis result with summary
export interface AnalyzeResultDto {
  shipmentId: string;
  shipmentRefNumber: string;
  message?: string;
  summary?: {
    totalExpectedItems: number;
    totalReceivedItems: number;
    positiveDiscrepancies: number;
    negativeDiscrepancies: number;
    hasDiscrepancy: boolean;
  };
}

// Report generation result
export interface ReportResultDto {
  shipmentId: string;
  shipmentRefNumber: string;
  reportUrl?: string;
  message?: string;
}